/**
 * @file src/components/ProtectedRoute.jsx
 * @description Route wrapper that enforces authentication before rendering component
 * 
 * Redirect unauthenticated users to login page, allowing only logged-in users
 * to access protected pages (CapsuleDetail, CapsuleWizard, Profile, etc).
 * 
 * Flow:
 * 1. Check if user is authenticated (useAuth context)
 * 2. While loading, show nothing (or skeleton)
 * 3. If authenticated → render children component
 * 4. If not authenticated → redirect to /login with ?return= parameter
 * 
 * The return= query param tells login page where to redirect after successful auth.
 * Example: User tries /capsuleDetail?id=123 → redirected to /login?return=/capsuleDetail  * 
 * @component
 * @param {Object} props
 * @param {React.ReactNode} props.children - Component to protect
 * @returns {React.ReactElement} Either children or <Navigate> to /login
 * 
 * @example
 *   // In App.jsx
 *   <Route path="/capsuleList" element={
 *     <ProtectedRoute>
 *       <CapsuleList />
 *     </ProtectedRoute>
 *   } />
 */

import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) return null;

  if (!user) {
    return <Navigate to="/" replace />;
  }

  return children;
};

export default ProtectedRoute;
